// ─────────────────────────────────────────────────────────────────────────────
// useResetPassword — token extraction, form state, validation, the reset call,
// and the post-success redirect (timer cleaned on unmount). Keeps the page
// presentational, matching useAcceptInvite. Unlike accept-invite, a missing token
// does NOT redirect — the page renders a "request a new link" state instead, so
// the user isn't silently bounced when an email link arrives malformed.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import authService from "../../../services/authService";
import { validatePasswordPolicy, validatePasswordMatch } from "../utils/validators";

export function useResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const togglePassword = () => setShowPassword((s) => !s);

  // Redirect to login after success, cleaning the timer on unmount.
  useEffect(() => {
    if (!done) return undefined;
    const id = setTimeout(() => navigate("/login", { replace: true }), 2000);
    return () => clearTimeout(id);
  }, [done, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    // Enforce the full complexity policy client-side for immediate feedback; the
    // backend re-checks the same rules (password-policy.ts) as the authority.
    const pwErr = validatePasswordPolicy(form.password) || validatePasswordMatch(form.password, form.confirm);
    if (pwErr) { setError(pwErr); return; }
    setError("");
    setLoading(true);
    try {
      await authService.resetPassword(token, form.password);
      setDone(true);
    } catch (err) {
      setError(err?.message || "Reset link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return { token, form, setField, showPassword, togglePassword, loading, error, done, submit };
}

export default useResetPassword;
